import { useState } from "react";

import noop from "lodash/noop";

import { Modal } from "@similarweb/ui-components";

import Buttons from "app/pages/activation/components/SetupModal/Buttons/Buttons";
import { useAISetup } from "app/pages/activation/components/SetupModal/hooks/useAISetup";
import { useSetupCompetitors } from "app/pages/activation/components/SetupModal/hooks/useSetupCompetitors";
import { useSetupName } from "app/pages/activation/components/SetupModal/hooks/useSetupName";
import { activationTracking } from "app/pages/activation/services/tracking";

import { WithKeysInput } from "../../hoc/withKeysInput";

import { ButtonsAlignVariant } from "./Buttons/types";
import CompetitorsStep from "./CompetitorsStep/CompetitorsStep";
import {
    getBackButton,
    getCloseButton,
    DEFAULT_ACTIVATION_SET_NAME,
    getFinishButton,
    modalConfig,
    nameModalConfig,
    SETUP_STEPS,
    SETUP_STEPS_NAMES,
} from "./constants";
import NameStep from "./NameStep/NameStep";
import { getOpenText, getSaveText } from "./texts";
import type { SetupModalSchema } from "./types";

export const SetupModal = ({
    isOpen,
    selectedPrimaryEntity,
    onClose,
    existingNames,
    resetStateOnClose,
}: SetupModalSchema) => {
    const [currentStep, setCurrentStep] = useState(SETUP_STEPS.SELECT_COMPETITORS);
    const isSelectCompetitorsStep = SETUP_STEPS[currentStep] === SETUP_STEPS_NAMES.SELECT_COMPETITORS;
    const isNameStep = SETUP_STEPS[currentStep] === SETUP_STEPS_NAMES.ENTER_NAME;
    const track = activationTracking.setup;

    const { isAIInputFocused, handleAIInputFocus, handleAIInputBlur } = useAISetup();

    const {
        primary,
        setPrimary,
        savedThreadId,
        setSavedThreadId,
        selectedCompetitors,
        setSelectedCompetitors,
        onCompetitorSelect,
        selectedCountry,
        onCountrySelect,
    } = useSetupCompetitors({ selectedPrimaryEntity, isOpen });

    const {
        onNameChange,
        nameError,
        activationSetName,
        setActivationSetName,
        validateName,
        shouldSubscribe,
        onSubscribeToggle,
        isCreating,
        isSubscribing,
        onCreate,
        isNameStepDisabled,
    } = useSetupName({
        existingNames,
        selectedCompetitors,
        selectedCountry,
        isNameStep,
        primary,
    });

    const handleNext = () => {
        setCurrentStep((step) => step + 1);

        if (isSelectCompetitorsStep) {
            const defaultName = `${DEFAULT_ACTIVATION_SET_NAME} ${primary?.name}`;

            validateName(defaultName);
            setActivationSetName(defaultName);
        }
    };

    const handleBack = () => {
        track.trackCancelSetup(SETUP_STEPS_NAMES.ENTER_NAME);
        setCurrentStep((step) => step - 1);
    };

    const resetStates = () => {
        setSelectedCompetitors([]);
        setActivationSetName("");
        setPrimary(null);
    };

    const onCloseClick = () => {
        setCurrentStep(SETUP_STEPS.SELECT_COMPETITORS);

        const step = isNameStep ? SETUP_STEPS_NAMES.ENTER_NAME : SETUP_STEPS_NAMES.SELECT_COMPETITORS;

        track.trackCancelSetup(step);
        onClose();

        if (resetStateOnClose) {
            resetStates();
        }
    };

    const isNextDisabled = !selectedCompetitors.length || !primary || isCreating || isSubscribing || isNameStepDisabled;

    const onEnterHandler = isAIInputFocused ? noop : handleNext;

    // just removed AiCompetitorsEnabled from useAISetup to left AI Competitors step for everyone.
    // have option to back logic with DefaultCompetitorsStep (app/pages/activation/components/SetupModal/DefaultCompetitorsStep/DefaultCompetitorsStep.tsx)
    // left this comment to comfort rewert
    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onCloseClick}
            shouldCloseOnOverlayClick={!isNameStep}
            customStyles={!isNameStep ? modalConfig : nameModalConfig}
            data-automation="basic-modal"
        >
            <WithKeysInput
                isEnterDisabled={isNextDisabled}
                onEnter={isNameStep ? onCreate : onEnterHandler}
            >
                {isSelectCompetitorsStep ? (
                    <CompetitorsStep
                        savedThreadId={savedThreadId}
                        setSavedThreadId={setSavedThreadId}
                        showWebsiteSelect={!selectedPrimaryEntity}
                        setPrimary={setPrimary}
                        selectedPrimaryEntity={primary}
                        selectedCompetitors={selectedCompetitors}
                        selectedCountry={selectedCountry}
                        onCompetitorSelect={onCompetitorSelect}
                        setSelectedCompetitors={setSelectedCompetitors}
                        onCountrySelect={onCountrySelect}
                        handleFocus={handleAIInputFocus}
                        handleBlur={handleAIInputBlur}
                        isOpen={isOpen}
                    />
                ) : null}

                {isNameStep ? (
                    <NameStep
                        onNameChange={onNameChange}
                        errorState={nameError}
                        activationSetName={activationSetName}
                        shouldSubscribe={shouldSubscribe}
                        onSubscribeToggle={onSubscribeToggle}
                    />
                ) : null}

                <Buttons
                    showCancelButton
                    closeButton={
                        isNameStep
                            ? { ...getBackButton(), onClick: handleBack }
                            : { ...getCloseButton(), onClick: onCloseClick }
                    }
                    nextButton={{
                        ...getFinishButton(),
                        isDisabled: isNextDisabled,
                        onClick: isNameStep ? onCreate : handleNext,
                        isLoading: isCreating || isSubscribing,
                        label: isNameStep
                            ? !selectedPrimaryEntity
                                ? getSaveText()
                                : getOpenText()
                            : getFinishButton().label,
                    }}
                    alignButtons={isNameStep ? ButtonsAlignVariant.End : ButtonsAlignVariant.SpaceBetween}
                />
            </WithKeysInput>
        </Modal>
    );
};
